// RECRUITMENT-CANDIDATE-PROFILE-UI-002 — Candidate Profile workspace (shared by Candidates list + Pipeline Board)
import React, { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Loader2 } from 'lucide-react';
import { isHrReadOnly } from '../../../lib/hrRole';
import { Sheet, SheetContent, SheetTitle } from '../../ui/sheet';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../../ui/tabs';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription,
  AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '../../ui/alert-dialog';
import { profileFromCandidate, profileToPayload, validateProfile } from './CandidateProfileFields';
import { CandidateHeader } from './drawer/CandidateHeader';
import { EditProfilePanel } from './drawer/EditProfilePanel';
import { OverviewTab } from './drawer/OverviewTab';
import { PersonalTab, EducationTab, CompensationTab } from './drawer/ProfileTabs';
import { DocumentsTab } from './drawer/DocumentsTab';
import { RecruitmentTab } from './drawer/RecruitmentTab';
import { ActivityTab } from './drawer/ActivityTab';
import { stageLabel } from './drawer/drawerShared';

const RESUME_ACCEPT = '.pdf,.doc,.docx,application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document';
const RESUME_MAX = 10 * 1024 * 1024;
const currentRole = () => { try { return JSON.parse(localStorage.getItem('user') || '{}').role; } catch { return ''; } };
const API = process.env.REACT_APP_BACKEND_URL || '';
const EMPTY_INTERVIEW = { scheduled_at: '', interviewers: '', location: '', meeting_link: '', notes: '' };
const EMPTY_OFFER = { offer_date: '', salary: '', expiry_date: '', notes: '' };
const errMsg = (e, fallback) => { const d = e.response?.data?.detail; return typeof d === 'string' ? d : (Array.isArray(d) ? d[0]?.msg : fallback); };

const TABS = [
  ['overview', 'Overview'], ['personal', 'Personal'], ['education', 'Education & Experience'], ['compensation', 'Compensation'],
  ['documents', 'Documents'], ['recruitment', 'Recruitment'], ['activity', 'Activity'],
];

export const CandidateDetailDrawer = ({ candidateId, onClose, onChanged, onConvert, refreshKey = 0, editRequestKey = 0, navIds = [], onNavigate }) => {
  const hrReadOnly = isHrReadOnly();
  const canViewCtc = ['admin', 'hr_manager'].includes(currentRole());
  const [tab, setTab] = useState('overview');
  const [candidate, setCandidate] = useState(null);
  const [auditLog, setAuditLog] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [stageSaving, setStageSaving] = useState(false);
  const [uploadingResume, setUploadingResume] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const [profileForm, setProfileForm] = useState(null);
  const [rejectOpen, setRejectOpen] = useState(false);
  const [showInterviewForm, setShowInterviewForm] = useState(false);
  const [interviewForm, setInterviewForm] = useState(EMPTY_INTERVIEW);
  const [showOfferForm, setShowOfferForm] = useState(false);
  const [offerForm, setOfferForm] = useState(EMPTY_OFFER);
  const [showNoteForm, setShowNoteForm] = useState(false);
  const [newNote, setNewNote] = useState('');
  const resumeInputRef = useRef(null);
  const handledEditKey = useRef(0);

  const canEdit = !hrReadOnly && !!candidate && !candidate.is_converted;
  const canConvert = !!candidate && candidate.stage === 'hired' && !candidate.is_converted && !!onConvert && !hrReadOnly;

  const fetchDetail = useCallback(async () => {
    if (!candidateId) return;
    setLoading(true);
    try {
      const [detailRes, auditRes] = await Promise.all([
        axios.get(`${API}/api/recruitment/candidates/${candidateId}`),
        axios.get(`${API}/api/recruitment/audit-log?entity_type=candidate&entity_id=${candidateId}&limit=50`),
      ]);
      setCandidate(detailRes.data);
      setAuditLog(auditRes.data.data || []);
    } catch (err) {
      toast.error(errMsg(err, 'Failed to load candidate details'));
    } finally { setLoading(false); }
  }, [candidateId]);

  useEffect(() => {
    if (candidateId) {
      setCandidate(null); setEditOpen(false); setShowInterviewForm(false); setShowOfferForm(false); setShowNoteForm(false); setNewNote('');
      fetchDetail();
    } else { setTab('overview'); }
  }, [candidateId, fetchDetail]);

  // parent-triggered refetch (e.g. after Convert to Employee completes outside the drawer)
  useEffect(() => { if (refreshKey && candidateId) fetchDetail(); }, [refreshKey]); // eslint-disable-line react-hooks/exhaustive-deps

  // parent-requested Edit (row 'Edit' action) — switches the drawer to Edit Mode once the candidate is loaded
  useEffect(() => {
    if (editRequestKey && editRequestKey !== handledEditKey.current && candidate && canEdit) {
      handledEditKey.current = editRequestKey;
      setProfileForm(profileFromCandidate(candidate)); setEditOpen(true);
    }
  }, [editRequestKey, candidate, canEdit]);

  const refresh = () => { fetchDetail(); onChanged && onChanged(); };
  const run = async (fn, okMsg, failMsg, setBusy = setSaving) => {
    setBusy(true);
    try { const r = await fn(); if (okMsg) toast.success(typeof okMsg === 'function' ? okMsg(r) : okMsg); refresh(); return true; }
    catch (e) { toast.error(errMsg(e, failMsg)); return false; }
    finally { setBusy(false); }
  };

  const openEdit = () => { setProfileForm(profileFromCandidate(candidate)); setEditOpen(true); };
  const saveProfile = async () => {
    const err = validateProfile(profileForm);
    if (err) { toast.error(err); return; }
    if (await run(() => axios.put(`${API}/api/recruitment/candidates/${candidateId}`, profileToPayload(profileForm, canViewCtc)), 'Candidate profile updated', 'Failed to update profile')) setEditOpen(false);
  };

  const handleStageChange = (stage) => {
    if (!candidate || stage === candidate.stage) return;
    return run(() => axios.put(`${API}/api/recruitment/candidates/${candidateId}`, { stage }), `Stage updated to ${stageLabel(stage)}`, 'Failed to update stage', setStageSaving);
  };

  const handleResumeFile = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    const ext = file.name.split('.').pop().toLowerCase();
    if (!['pdf', 'doc', 'docx'].includes(ext)) { toast.error('Only PDF, DOC and DOCX resumes are allowed'); return; }
    if (file.size > RESUME_MAX) { toast.error('Resume exceeds the 10 MB limit'); return; }
    const fd = new FormData(); fd.append('file', file);
    await run(() => axios.post(`${API}/api/recruitment/candidates/${candidateId}/resume/proxy-upload`, fd, { headers: { 'Content-Type': 'multipart/form-data' } }),
      (r) => (r.data?.replaced ? 'Resume replaced' : 'Resume uploaded'), 'Resume upload failed — existing resume unchanged', setUploadingResume);
  };
  const resumeUrl = async () => (await axios.get(`${API}/api/recruitment/candidates/${candidateId}/resume/download-url`)).data;
  const handleViewResume = async () => {
    try { const d = await resumeUrl(); window.open(d.download_url, '_blank'); }
    catch (err) { toast.error(errMsg(err, 'Failed to open resume')); }
  };
  const handleSaveResume = async () => {
    try {
      const d = await resumeUrl();
      try {
        const blob = await (await fetch(d.download_url)).blob();
        const a = document.createElement('a'); a.href = URL.createObjectURL(blob); a.download = d.file_name || 'resume'; a.click();
        setTimeout(() => URL.revokeObjectURL(a.href), 4000);
      } catch { window.open(d.download_url, '_blank'); }
    } catch (err) { toast.error(errMsg(err, 'Failed to download resume')); }
  };

  const handleScheduleInterview = async () => {
    if (!interviewForm.scheduled_at) return toast.error('Please select the interview date and time');
    const ok = await run(() => axios.post(`${API}/api/recruitment/candidates/${candidateId}/interviews`, {
      scheduled_at: new Date(interviewForm.scheduled_at).toISOString(),
      interviewers: interviewForm.interviewers.split(',').map((s) => s.trim()).filter(Boolean),
      location: interviewForm.location.trim() || null, meeting_link: interviewForm.meeting_link.trim() || null, notes: interviewForm.notes.trim() || null,
    }), 'Interview scheduled', 'Failed to schedule interview');
    if (ok) { setInterviewForm(EMPTY_INTERVIEW); setShowInterviewForm(false); }
  };
  const handleInterviewChange = (interviewId, field, value) =>
    run(() => axios.put(`${API}/api/recruitment/candidates/${candidateId}/interviews/${interviewId}`, { [field]: value }), 'Interview updated', 'Failed to update interview');

  const handleCreateOffer = async () => {
    if (!offerForm.offer_date) return toast.error('Please select the offer date');
    const ok = await run(() => axios.post(`${API}/api/recruitment/candidates/${candidateId}/offer`, {
      offer_date: offerForm.offer_date, salary: offerForm.salary ? parseFloat(offerForm.salary) : null, currency: 'USD',
      expiry_date: offerForm.expiry_date || null, notes: offerForm.notes.trim() || null,
    }), 'Offer issued', 'Failed to create offer');
    if (ok) { setOfferForm(EMPTY_OFFER); setShowOfferForm(false); }
  };
  const handleOfferStatus = (status) =>
    run(() => axios.put(`${API}/api/recruitment/candidates/${candidateId}/offer/status`, { status }), `Offer marked as ${status}`, 'Failed to update offer status');

  const handleAddNote = async () => {
    if (!newNote.trim()) return toast.error('Please enter interview notes');
    const ok = await run(() => axios.post(`${API}/api/recruitment/candidates/${candidateId}/interview-notes`, { notes: newNote.trim() }), 'Note added', 'Failed to add note');
    if (ok) { setNewNote(''); setShowNoteForm(false); }
  };

  const navIndex = navIds.indexOf(candidateId);
  const nav = onNavigate && navIds.length > 1 ? {
    hasPrev: navIndex > 0, hasNext: navIndex >= 0 && navIndex < navIds.length - 1,
    onPrev: () => onNavigate(navIds[navIndex - 1]), onNext: () => onNavigate(navIds[navIndex + 1]),
    position: navIndex >= 0 ? `${navIndex + 1} of ${navIds.length}` : null,
  } : null;

  const resumeProps = { candidate, canEdit, uploading: uploadingResume, onPickFile: () => resumeInputRef.current?.click(), onView: handleViewResume, onDownload: handleSaveResume };
  const visibleTabs = TABS.filter(([k]) => k !== 'compensation' || canViewCtc);

  return (
    <Sheet open={!!candidateId} onOpenChange={(open) => { if (!open) onClose(); }}>
      <SheetContent className="sf-premium top-14 flex h-[calc(100vh-3.5rem)] w-full max-w-[96vw] flex-col gap-0 bg-slate-50 p-0 sm:max-w-none md:w-[760px] lg:w-[880px] xl:w-[980px]" data-testid="candidate-detail-drawer">
        {loading && !candidate ? (
          <div className="flex flex-1 items-center justify-center">
            <SheetTitle className="sr-only">Loading candidate</SheetTitle>
            <Loader2 className="h-8 w-8 animate-spin text-blue-600" />
          </div>
        ) : candidate ? (
          <>
            <CandidateHeader candidate={candidate} canEdit={canEdit} canConvert={canConvert} canReject={canEdit && candidate.stage !== 'rejected'}
              onEdit={openEdit} onConvert={() => onConvert(candidate)} onReject={() => setRejectOpen(true)} nav={nav} editing={editOpen} />
            {editOpen && profileForm ? (
              <EditProfilePanel value={profileForm} onChange={setProfileForm} showCtc={canViewCtc} saving={saving}
                onCancel={() => setEditOpen(false)} onSave={saveProfile} />
            ) : (
            <Tabs value={tab} onValueChange={setTab} className="flex min-h-0 flex-1 flex-col">
              <TabsList className="h-auto w-full shrink-0 justify-start gap-1 overflow-x-auto rounded-none border-b border-slate-200 bg-white px-4 py-2" data-testid="candidate-tabs">
                {visibleTabs.map(([k, l]) => (
                  <TabsTrigger key={k} value={k} data-testid={`tab-${k}`}
                    className="rounded-md px-3 py-1.5 text-xs font-semibold text-slate-600 data-[state=active]:bg-blue-50 data-[state=active]:text-blue-700 data-[state=active]:shadow-none">
                    {l}
                  </TabsTrigger>
                ))}
              </TabsList>
              <div className="min-h-0 flex-1 overflow-y-auto px-6 py-5">
                <TabsContent value="overview" className="mt-0"><OverviewTab candidate={candidate} canViewCtc={canViewCtc} resumeProps={resumeProps} /></TabsContent>
                <TabsContent value="personal" className="mt-0"><PersonalTab candidate={candidate} canEdit={canEdit} onEdit={openEdit} /></TabsContent>
                <TabsContent value="education" className="mt-0"><EducationTab candidate={candidate} canEdit={canEdit} onEdit={openEdit} /></TabsContent>
                {canViewCtc && <TabsContent value="compensation" className="mt-0"><CompensationTab candidate={candidate} canEdit={canEdit} onEdit={openEdit} /></TabsContent>}
                <TabsContent value="documents" className="mt-0"><DocumentsTab {...resumeProps} /></TabsContent>
                <TabsContent value="recruitment" className="mt-0">
                  <RecruitmentTab candidate={candidate} canEdit={canEdit} canConvert={canConvert} saving={saving} stageSaving={stageSaving}
                    onStageChange={handleStageChange} onConvert={() => onConvert(candidate)}
                    interviews={{ showForm: showInterviewForm, setShowForm: setShowInterviewForm, form: interviewForm, setForm: setInterviewForm, onSubmit: handleScheduleInterview, onChange: handleInterviewChange }}
                    offer={{ showForm: showOfferForm, setShowForm: setShowOfferForm, form: offerForm, setForm: setOfferForm, onSubmit: handleCreateOffer, onStatus: handleOfferStatus }}
                    notes={{ showForm: showNoteForm, setShowForm: setShowNoteForm, value: newNote, setValue: setNewNote, onSubmit: handleAddNote }} />
                </TabsContent>
                <TabsContent value="activity" className="mt-0"><ActivityTab auditLog={auditLog} /></TabsContent>
              </div>
            </Tabs>
            )}
            <input ref={resumeInputRef} type="file" accept={RESUME_ACCEPT} className="hidden" onChange={handleResumeFile} data-testid="drawer-resume-file-input" />
          </>
        ) : (
          <div className="p-6 text-sm text-slate-400">
            <SheetTitle className="sr-only">Candidate not found</SheetTitle>
            Candidate not found.
          </div>
        )}

        <AlertDialog open={rejectOpen} onOpenChange={setRejectOpen}>
          <AlertDialogContent data-testid="reject-confirm-dialog">
            <AlertDialogHeader>
              <AlertDialogTitle>Reject this candidate?</AlertDialogTitle>
              <AlertDialogDescription>{candidate?.name} will be moved to the Rejected stage. You can reopen the application later via “Change Stage”.</AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel data-testid="reject-cancel-btn">Cancel</AlertDialogCancel>
              <AlertDialogAction className="bg-rose-600 hover:bg-rose-700" onClick={() => handleStageChange('rejected')} data-testid="reject-confirm-btn">Reject Candidate</AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </SheetContent>
    </Sheet>
  );
};

export default CandidateDetailDrawer;
